/**
 * Margins.ts - Margin adjustment functions for WTools Mac
 *
 * Provides functions to adjust text margins on selected PowerPoint shapes.
 */

/**
 * Adjusts all margins (left, right, top, bottom) on selected shapes by a delta value.
 * @param delta - The amount to add to each margin (in points). Use negative for decrease.
 */
export async function adjustMargins(delta: number): Promise<void> {
  await PowerPoint.run(async (context) => {
    const selectedShapes = context.presentation.getSelectedShapes();
    selectedShapes.load("items");
    await context.sync();

    if (selectedShapes.items.length === 0) {
      console.warn("No shapes selected");
      return;
    }

    // Load current margin values for all shapes
    for (const shape of selectedShapes.items) {
      shape.textFrame.load(["leftMargin", "rightMargin", "topMargin", "bottomMargin"]);
    }
    await context.sync();

    // Apply delta to all margins, ensuring they don't go negative
    for (const shape of selectedShapes.items) {
      shape.textFrame.leftMargin = Math.max(0, shape.textFrame.leftMargin + delta);
      shape.textFrame.rightMargin = Math.max(0, shape.textFrame.rightMargin + delta);
      shape.textFrame.topMargin = Math.max(0, shape.textFrame.topMargin + delta);
      shape.textFrame.bottomMargin = Math.max(0, shape.textFrame.bottomMargin + delta);
    }

    await context.sync();
  });
}

/**
 * Adjusts only the left margin on selected shapes by a delta value.
 * @param delta - The amount to add to the left margin (in points). Use negative for decrease.
 */
export async function adjustLeftMargin(delta: number): Promise<void> {
  await PowerPoint.run(async (context) => {
    const selectedShapes = context.presentation.getSelectedShapes();
    selectedShapes.load("items");
    await context.sync();

    if (selectedShapes.items.length === 0) {
      console.warn("No shapes selected");
      return;
    }

    // Load current left margin for all shapes
    for (const shape of selectedShapes.items) {
      shape.textFrame.load("leftMargin");
    }
    await context.sync();

    // Apply delta to left margin only, ensuring it doesn't go negative
    for (const shape of selectedShapes.items) {
      shape.textFrame.leftMargin = Math.max(0, shape.textFrame.leftMargin + delta);
    }

    await context.sync();
  });
}

/**
 * Removes all margins from selected shapes (sets all to 0).
 */
export async function removeMargins(): Promise<void> {
  await PowerPoint.run(async (context) => {
    const selectedShapes = context.presentation.getSelectedShapes();
    selectedShapes.load("items");
    await context.sync();

    if (selectedShapes.items.length === 0) {
      console.warn("No shapes selected");
      return;
    }

    // Set all margins to 0
    for (const shape of selectedShapes.items) {
      shape.textFrame.leftMargin = 0;
      shape.textFrame.rightMargin = 0;
      shape.textFrame.topMargin = 0;
      shape.textFrame.bottomMargin = 0;
    }

    await context.sync();
  });
}

/**
 * Removes only the left margin from selected shapes (sets left margin to 0).
 */
export async function removeLeftMargin(): Promise<void> {
  await PowerPoint.run(async (context) => {
    const selectedShapes = context.presentation.getSelectedShapes();
    selectedShapes.load("items");
    await context.sync();

    if (selectedShapes.items.length === 0) {
      console.warn("No shapes selected");
      return;
    }

    // Set left margin to 0
    for (const shape of selectedShapes.items) {
      shape.textFrame.leftMargin = 0;
    }

    await context.sync();
  });
}
